import React, { useEffect, useMemo, useCallback } from "react";
import { Container, Row, Col, Button, Card, Badge, Nav } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

const TeaSof = () => {
  const navigate = useNavigate();

  // ===================== THEME =====================
  const theme = useMemo(
    () => ({
      bg: "#f6f8fb",
      surface: "#ffffff",
      ink: "#0f172a",
      slate: "#334155",
      muted: "#64748b",
      border: "#e5e7eb",
      navy: "#0b1220",
      accent: "#1d4ed8",
      accent2: "#0ea5e9",
      success: "#16a34a",
      shadow: "0 24px 48px rgba(2, 6, 23, 0.08)",
      shadowSoft: "0 12px 24px rgba(2, 6, 23, 0.06)",
    }),
    []
  );

  // ===================== CONFIG =====================
  const brand = useMemo(
    () => ({
      name: "EBS Software Solution",
      email: "info@ebssoftware.co.in",
      phoneTop: "+91 99320 13501",
      salesPhone: "+91 97337 2 1111",
      gst: "19AJAPM8399K1ZE",
      websiteUrl: "https://example.com", // change to your site
      pageUrl: "https://example.com/teasof", // change to your teasof page url
    }),
    []
  );

  const product = useMemo(
    () => ({
      name: "TeaSof ERP",
      tagline: "Tea Estate Management ERP",
      description:
        "TeaSof ERP is a purpose-built Tea Garden Management system covering wages, attendance, inventory, accounting, payroll and factory operations — with Android attendance and weighing scale integration.",
    }),
    []
  );

  const heroImage = useMemo(
    () =>
      "https://images.unsplash.com/photo-1586351012024-8b1d917f8a7d?auto=format&fit=crop&q=80&w=2000",
    []
  );

  // ===================== CONTENT =====================
  const trustStats = useMemo(
    () => [
      { label: "Deployment", value: "Windows + Android" },
      { label: "Best For", value: "Tea Estates / Tea Gardens" },
      { label: "Integrations", value: "Weighing Scales + Mobile" },
      { label: "Outputs", value: "Wage / PF-ECR / Stock / Finance" },
    ],
    []
  );

  const featureCards = useMemo(
    () => [
      {
        title: "Wages & Attendance",
        desc: "Attendance capture, wage sheets, wage slips and statutory-ready outputs.",
      },
      {
        title: "Inventory & Purchase",
        desc: "Stock visibility, min/max alerts, PO tracking and consumption analysis.",
      },
      {
        title: "Factory Operations",
        desc: "Plucking weighment automation and production tracking support.",
      },
      {
        title: "Accounting & Reporting",
        desc: "Cash/bank books, ledgers, trial balance, cost centres and analytics.",
      },
      {
        title: "Staff Payroll",
        desc: "Payroll registers, professional tax, PF and arrears processing.",
      },
      {
        title: "Mobile Field App",
        desc: "Android app support for attendance workflows and real-time updates.",
      },
    ],
    []
  );

  const modules = useMemo(
    () => [
      {
        title: "Workforce & Wages",
        bullets: [
          "Casual, Permanent & Contract Attendance",
          "Wage Sheet Generation & Summaries",
          "Digital Wage Slips + Payment Statements",
          "PF Forms (3A, 6A) & ECR Challan",
          "Bonus, Leave & Incentive Registers",
        ],
      },
      {
        title: "Inventory & Purchase",
        bullets: [
          "Product/Division wise Stock Status",
          "Min/Max Level Alerts",
          "Purchase Orders (Pending / Done)",
          "Consumption Analysis (Dept/Section wise)",
          "Supplier & Division wise Reporting",
        ],
      },
      {
        title: "Staff Payroll",
        bullets: [
          "Staff Pay Register & Slips",
          "PF & Professional Tax Register",
          "Annual Leave & Arrear Pay",
          "Deductions (LIC, etc.)",
        ],
      },
      {
        title: "Accounting & Finance",
        bullets: [
          "Cash & Bank Books",
          "Journal, Ledger & Trial Balance",
          "Cost Centre Analysis",
          "Monthly Expenditure Statements",
          "Outstanding Analysis (Bill by Bill)",
        ],
      },
    ],
    []
  );

  // ===================== SEO (JSON-LD + Meta) =====================
  const schemaMarkup = useMemo(
    () => ({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Organization",
          name: brand.name,
          url: brand.websiteUrl,
          email: brand.email,
          telephone: brand.phoneTop,
        },
        {
          "@type": "WebSite",
          "@id": `${brand.pageUrl}#website`,
          name: brand.name,
          url: brand.websiteUrl,
        },
        {
          "@type": "WebPage",
          "@id": brand.pageUrl,
          url: brand.pageUrl,
          name: `${product.name} | Tea Garden Management ERP | ${brand.name}`,
          description: product.description,
          isPartOf: { "@id": `${brand.pageUrl}#website` },
        },
        {
          "@type": "SoftwareApplication",
          name: product.name,
          applicationCategory: "BusinessApplication",
          operatingSystem: ["Windows", "Android"],
          description: product.description,
          offers: {
            "@type": "Offer",
            priceCurrency: "INR",
            availability: "https://schema.org/InStock",
          },
          featureList: [
            "Worker Wages Management",
            "Attendance Management",
            "Inventory & Purchase Control",
            "Accounting & Reporting",
            "Staff Payroll",
            "Android Attendance System",
            "Weighing Scale Integration",
          ],
          publisher: { "@type": "Organization", name: brand.name },
        },
      ],
    }),
    [brand, product]
  );

  useEffect(() => {
    const prevTitle = document.title;
    document.title = `${product.name} | Tea Garden Management ERP | ${brand.name}`;

    const head = document.head;
    const cleanup = () => {
      head.querySelectorAll('[data-teasof="1"]').forEach((n) => n.remove());
    };
    cleanup();

    const addMeta = (attrs) => {
      const el = document.createElement("meta");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute("data-teasof", "1");
      head.appendChild(el);
    };

    const addLink = (attrs) => {
      const el = document.createElement("link");
      Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
      el.setAttribute("data-teasof", "1");
      head.appendChild(el);
    };

    addLink({ rel: "canonical", href: brand.pageUrl });
    addMeta({ name: "description", content: product.description });

    addMeta({ property: "og:type", content: "website" });
    addMeta({ property: "og:title", content: `${product.name} | ${brand.name}` });
    addMeta({ property: "og:description", content: product.description });
    addMeta({ property: "og:url", content: brand.pageUrl });
    addMeta({ property: "og:image", content: heroImage });

    addMeta({ name: "twitter:card", content: "summary_large_image" });
    addMeta({ name: "twitter:title", content: `${product.name} | ${brand.name}` });
    addMeta({ name: "twitter:description", content: product.description });
    addMeta({ name: "twitter:image", content: heroImage });

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify(schemaMarkup);
    script.setAttribute("data-teasof", "1");
    head.appendChild(script);

    return () => {
      cleanup();
      document.title = prevTitle;
    };
  }, [brand, heroImage, product, schemaMarkup]);

  // ===================== HELPERS =====================
  const scrollToId = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, []);

  const IconChip = () => (
    <span
      aria-hidden="true"
      style={{
        width: 36,
        height: 36,
        borderRadius: 14,
        background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent2})`,
        boxShadow: theme.shadowSoft,
        display: "inline-block",
        flexShrink: 0,
      }}
    />
  );

  // ===================== STYLES =====================
  const styles = {
    page: {
      background: theme.bg,
      minHeight: "100vh",
      color: theme.slate,
      fontFamily:
        "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    },

    topBar: {
      background: theme.navy,
      color: "#e2e8f0",
      fontSize: 13,
      borderBottom: "1px solid rgba(255,255,255,0.08)",
    },

    topBarInner: {
      padding: "8px 0",
      display: "flex",
      justifyContent: "space-between",
      gap: 12,
      flexWrap: "wrap",
      alignItems: "center",
    },

    topLink: {
      color: "#e2e8f0",
      textDecoration: "none",
      fontWeight: 800,
    },

    navWrap: {
      position: "sticky",
      top: 0,
      zIndex: 50,
      background: "rgba(246,248,251,0.9)",
      backdropFilter: "blur(12px)",
      borderBottom: `1px solid ${theme.border}`,
    },

    navInner: {
      padding: "14px 0",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 14,
      flexWrap: "wrap",
    },

    brand: { display: "flex", alignItems: "center", gap: 12 },
    logo: {
      width: 44,
      height: 44,
      borderRadius: 14,
      background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent2})`,
      boxShadow: theme.shadowSoft,
      flexShrink: 0,
    },
    brandName: {
      margin: 0,
      fontWeight: 950,
      color: theme.ink,
      letterSpacing: -0.2,
      fontSize: 16,
    },
    brandSub: { margin: 0, color: theme.muted, fontWeight: 750, fontSize: 12 },

    hero: {
      marginTop: 18,
      borderRadius: 24,
      overflow: "hidden",
      position: "relative",
      background: theme.navy,
      boxShadow: theme.shadow,
      border: `1px solid ${theme.border}`,
    },

    heroImg: {
      width: "100%",
      height: 540,
      objectFit: "cover",
      opacity: 0.22,
      filter: "grayscale(100%) contrast(120%)",
      transform: "scale(1.02)",
    },

    heroOverlay: {
      position: "absolute",
      inset: 0,
      background:
        "radial-gradient(900px 600px at 18% 25%, rgba(29,78,216,0.35), transparent 60%), radial-gradient(900px 500px at 85% 15%, rgba(14,165,233,0.25), transparent 55%), linear-gradient(180deg, rgba(11,18,32,0.50), rgba(11,18,32,0.92))",
    },

    heroContent: {
      position: "absolute",
      inset: 0,
      padding: "clamp(18px, 4vw, 56px)",
      display: "flex",
      alignItems: "center",
    },

    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      padding: "10px 14px",
      borderRadius: 999,
      background: "rgba(255,255,255,0.10)",
      border: "1px solid rgba(255,255,255,0.18)",
      color: "#dbeafe",
      fontWeight: 900,
      textTransform: "uppercase",
      letterSpacing: 1,
      fontSize: 12,
      width: "fit-content",
    },

    h1: {
      marginTop: 14,
      marginBottom: 10,
      fontSize: "clamp(2.2rem, 4.2vw, 3.6rem)",
      fontWeight: 950,
      color: "#fff",
      letterSpacing: -0.8,
      lineHeight: 1.05,
      textShadow: "0 10px 30px rgba(0,0,0,0.35)",
    },

    heroP: {
      color: "#e2e8f0",
      lineHeight: 1.9,
      fontSize: 16,
      maxWidth: 680,
      marginBottom: 18,
    },

    tagRow: { display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 },

    tag: {
      padding: "8px 12px",
      borderRadius: 999,
      color: "#e2e8f0",
      background: "rgba(255,255,255,0.10)",
      border: "1px solid rgba(255,255,255,0.18)",
      fontWeight: 850,
      fontSize: 13,
    },

    heroCard: {
      background: "rgba(255,255,255,0.97)",
      borderRadius: 18,
      border: "1px solid rgba(255,255,255,0.60)",
      boxShadow: "0 30px 70px rgba(2,6,23,0.25)",
    },

    heroCardTitle: {
      margin: 0,
      fontWeight: 950,
      color: theme.ink,
      fontSize: 15,
    },
    heroCardSub: {
      margin: "6px 0 0 0",
      color: theme.muted,
      fontWeight: 750,
      fontSize: 12,
    },

    statGrid: {
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12,
      marginTop: 14,
    },
    stat: {
      border: `1px solid ${theme.border}`,
      borderRadius: 14,
      padding: 12,
      background: theme.surface,
    },
    statLabel: { margin: 0, fontSize: 12, color: theme.muted, fontWeight: 850 },
    statValue: {
      margin: "6px 0 0 0",
      fontSize: 14,
      color: theme.ink,
      fontWeight: 950,
      lineHeight: 1.35,
    },

    section: { padding: "56px 0" },
    sectionTitle: {
      margin: 0,
      fontSize: 28,
      fontWeight: 950,
      color: theme.ink,
      letterSpacing: -0.4,
    },
    sectionSub: {
      marginTop: 10,
      marginBottom: 0,
      color: theme.muted,
      lineHeight: 1.85,
      maxWidth: 820,
    },

    featureCard: {
      height: "100%",
      borderRadius: 18,
      border: `1px solid ${theme.border}`,
      background: theme.surface,
      boxShadow: theme.shadowSoft,
      padding: 18,
    },
    featureTitle: {
      margin: 0,
      fontSize: 16,
      fontWeight: 950,
      color: theme.ink,
      letterSpacing: -0.2,
    },
    featureDesc: {
      margin: "8px 0 0 0",
      color: theme.muted,
      lineHeight: 1.8,
      fontSize: 14,
    },

    moduleCard: {
      height: "100%",
      borderRadius: 18,
      border: `1px solid ${theme.border}`,
      background: theme.surface,
      boxShadow: theme.shadowSoft,
      overflow: "hidden",
    },
    moduleHead: {
      padding: 18,
      borderBottom: `1px solid ${theme.border}`,
      background:
        "linear-gradient(180deg, rgba(29,78,216,0.08), rgba(255,255,255,0))",
    },
    moduleTitle: { margin: 0, fontWeight: 950, color: theme.ink, fontSize: 16 },
    moduleList: {
      listStyle: "none",
      margin: 0,
      padding: 18,
      display: "grid",
      gap: 10,
    },
    moduleItem: {
      display: "flex",
      gap: 10,
      alignItems: "flex-start",
      color: theme.slate,
      lineHeight: 1.75,
      fontSize: 14,
    },

    // ✅ FIXED CTA band (high contrast)
    cta: {
      borderRadius: 26,
      background: theme.navy,
      color: "#fff",
      position: "relative",
      overflow: "hidden",
      boxShadow: theme.shadow,
      border: "1px solid rgba(255,255,255,0.12)",
      marginTop: 6,
    },
    ctaOverlay: {
      position: "absolute",
      inset: 0,
      background:
        "radial-gradient(900px 500px at 20% 30%, rgba(29,78,216,0.45), transparent 60%), radial-gradient(800px 450px at 80% 20%, rgba(14,165,233,0.30), transparent 55%), linear-gradient(180deg, rgba(11,18,32,0.35), rgba(11,18,32,0.92))",
    },
    ctaBody: {
      position: "relative",
      padding: "30px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 16,
      flexWrap: "wrap",
    },
    ctaTitle: {
      margin: 0,
      fontWeight: 950,
      letterSpacing: -0.4,
      color: "#ffffff",
      fontSize: "clamp(1.4rem, 2.2vw, 2rem)",
      textShadow: "0 10px 26px rgba(0,0,0,0.35)",
    },
    ctaText: {
      margin: "10px 0 0 0",
      color: "#dbeafe",
      lineHeight: 1.85,
      fontSize: 15,
      maxWidth: 760,
    },
    footer: { padding: "26px 0 40px", color: theme.muted, fontSize: 13 },
  };

  return (
    <div style={styles.page}>
      {/* --- Top Contact Bar --- */}
      <div style={styles.topBar}>
        <Container>
          <div style={styles.topBarInner}>
            <div className="d-flex gap-3 flex-wrap">
              <span>
                Email:{" "}
                <a style={styles.topLink} href={`mailto:${brand.email}`}>
                  {brand.email}
                </a>
              </span>
              <span>
                Phone:{" "}
                <a
                  style={styles.topLink}
                  href={`tel:${brand.phoneTop.replace(/\s/g, "")}`}
                >
                  {brand.phoneTop}
                </a>
              </span>
            </div>
            <div style={{ fontWeight: 900 }}>GSTIN: {brand.gst}</div>
          </div>
        </Container>
      </div>

      {/* --- Corporate Navbar --- */}
      <div style={styles.navWrap}>
        <Container>
          <div style={styles.navInner}>
            <div style={styles.brand}>
              <div style={styles.logo} aria-hidden="true" />
              <div>
                <p style={styles.brandName}>{brand.name}</p>
                <p style={styles.brandSub}>
                  {product.name} • {product.tagline}
                </p>
              </div>
            </div>

            <Nav className="d-none d-md-flex" style={{ gap: 14 }}>
              <Nav.Link
                onClick={() => scrollToId("overview")}
                style={{ color: theme.slate, fontWeight: 850 }}
              >
                Overview
              </Nav.Link>
              <Nav.Link
                onClick={() => scrollToId("features")}
                style={{ color: theme.slate, fontWeight: 850 }}
              >
                Features
              </Nav.Link>
              <Nav.Link
                onClick={() => scrollToId("modules")}
                style={{ color: theme.slate, fontWeight: 850 }}
              >
                Modules
              </Nav.Link>
              <Nav.Link
                onClick={() => scrollToId("contact")}
                style={{ color: theme.slate, fontWeight: 850 }}
              >
                Contact
              </Nav.Link>
            </Nav>

            <div className="d-flex gap-2 flex-wrap">
              <Button
                variant="outline-primary"
                style={{ borderRadius: 12, fontWeight: 900 }}
                onClick={() => navigate("/request-demo")}
              >
                Request Demo
              </Button>
              <Button
                variant="primary"
                style={{
                  borderRadius: 12,
                  fontWeight: 950,
                  background: theme.accent,
                  borderColor: theme.accent,
                }}
                onClick={() => navigate("/contact-sales")}
              >
                Contact Sales
              </Button>
            </div>
          </div>
        </Container>
      </div>

      <Container>
        {/* --- Hero --- */}
        <div style={styles.hero}>
          <img
            src={heroImage}
            alt="Tea garden landscape background"
            style={styles.heroImg}
            loading="lazy"
          />
          <div style={styles.heroOverlay} />

          <div style={styles.heroContent}>
            <Row className="w-100 g-4 align-items-center">
              <Col lg={7}>
                <div style={styles.pill}>
                  <span
                    aria-hidden="true"
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: 999,
                      background: theme.accent2,
                    }}
                  />
                  Industry-grade ERP for Tea Estates
                </div>

                <h1 style={styles.h1}>{product.name}</h1>
                <p style={styles.heroP}>{product.description}</p>

                <div className="d-flex gap-2 flex-wrap">
                  <Button
                    variant="primary"
                    style={{
                      borderRadius: 12,
                      fontWeight: 950,
                      background: theme.accent2,
                      borderColor: theme.accent2,
                    }}
                    onClick={() => navigate("/request-demo")}
                  >
                    Book a Demo
                  </Button>

                  <Button
                    variant="light"
                    style={{ borderRadius: 12, fontWeight: 950 }}
                    onClick={() => scrollToId("modules")}
                  >
                    View Modules
                  </Button>

                  <Badge
                    bg="light"
                    text="dark"
                    style={{
                      borderRadius: 999,
                      padding: "10px 12px",
                      fontWeight: 900,
                    }}
                  >
                    Android + Scale Integration
                  </Badge>
                </div>

                <div style={styles.tagRow}>
                  {[
                    "Wages & Attendance",
                    "Inventory Control",
                    "Accounting & Reports",
                    "Factory Operations",
                  ].map((t) => (
                    <span key={t} style={styles.tag}>
                      {t}
                    </span>
                  ))}
                </div>
              </Col>

              <Col lg={5}>
                <Card style={styles.heroCard}>
                  <Card.Body>
                    <div className="d-flex align-items-center justify-content-between">
                      <div>
                        <p style={styles.heroCardTitle}>Executive Summary</p>
                        <p style={styles.heroCardSub}>
                          A quick fit check for your estate
                        </p>
                      </div>
                      <Badge
                        bg="primary"
                        style={{ borderRadius: 999, fontWeight: 900 }}
                      >
                        Enterprise
                      </Badge>
                    </div>

                    <div style={styles.statGrid}>
                      {trustStats.map((s) => (
                        <div key={s.label} style={styles.stat}>
                          <p style={styles.statLabel}>{s.label}</p>
                          <p style={styles.statValue}>{s.value}</p>
                        </div>
                      ))}
                    </div>

                    <div
                      className="mt-3"
                      style={{
                        borderTop: `1px solid ${theme.border}`,
                        paddingTop: 12,
                        color: theme.muted,
                        fontSize: 13,
                        lineHeight: 1.7,
                      }}
                    >
                      <b style={{ color: theme.ink }}>Sales:</b>{" "}
                      <a href={`tel:${brand.salesPhone.replace(/\s/g, "")}`}>
                        {brand.salesPhone}
                      </a>
                      <br />
                      <span>
                        Includes wage slips, PF/ECR outputs, stock control,
                        purchase tracking, books & reports, plus Android
                        attendance workflows.
                      </span>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </div>
        </div>

        {/* --- Overview --- */}
        <section id="overview" style={styles.section}>
          <Row className="g-4 align-items-start">
            <Col lg={5}>
              <h2 style={styles.sectionTitle}>Overview</h2>
              <p style={styles.sectionSub}>
                TeaSof ERP is engineered for tea estate operations — connecting
                field data, factory workflows, inventory, and finance into one
                system.
              </p>
            </Col>

            <Col lg={7}>
              <Row className="g-3">
                {featureCards.slice(0, 4).map((f) => (
                  <Col md={6} key={f.title}>
                    <div style={styles.featureCard}>
                      <div className="d-flex align-items-center gap-2">
                        <IconChip />
                        <h3 style={styles.featureTitle}>{f.title}</h3>
                      </div>
                      <p style={styles.featureDesc}>{f.desc}</p>
                    </div>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </section>

        {/* --- Features --- */}
        <section id="features" style={{ ...styles.section, paddingTop: 0 }}>
          <Row className="mb-3">
            <Col lg={8}>
              <h2 style={styles.sectionTitle}>Key Capabilities</h2>
              <p style={styles.sectionSub}>
                Corporate-grade controls and estate-ready outputs designed for
                real operating conditions.
              </p>
            </Col>
          </Row>

          <Row className="g-3">
            {featureCards.map((f) => (
              <Col md={6} lg={4} key={f.title}>
                <div style={styles.featureCard}>
                  <div className="d-flex align-items-center justify-content-between">
                    <h3 style={styles.featureTitle}>{f.title}</h3>
                    <span
                      aria-hidden="true"
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: 999,
                        background: theme.accent2,
                      }}
                    />
                  </div>
                  <p style={styles.featureDesc}>{f.desc}</p>
                </div>
              </Col>
            ))}
          </Row>
        </section>

        {/* --- Modules --- */}
        <section id="modules" style={styles.section}>
          <Row className="mb-3">
            <Col lg={8}>
              <h2 style={styles.sectionTitle}>Core Modules</h2>
              <p style={styles.sectionSub}>
                Modular design so estates can start quickly and scale
                capabilities over time.
              </p>
            </Col>
          </Row>

          <Row className="g-3">
            {modules.map((m) => (
              <Col lg={6} key={m.title}>
                <Card style={styles.moduleCard}>
                  <div style={styles.moduleHead}>
                    <h3 style={styles.moduleTitle}>{m.title}</h3>
                  </div>

                  <ul style={styles.moduleList}>
                    {m.bullets.map((b) => (
                      <li key={b} style={styles.moduleItem}>
                        <span
                          aria-hidden="true"
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: 999,
                            background: theme.success,
                            marginTop: 6,
                            flexShrink: 0,
                          }}
                        />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </Col>
            ))}
          </Row>
        </section>

        {/* --- CTA / Contact --- */}
        <section id="contact" style={{ paddingBottom: 56 }}>
          <div style={styles.cta}>
            <div style={styles.ctaOverlay} />
            <div style={styles.ctaBody}>
              <div>
                <h2 style={styles.ctaTitle}>Want a demo tailored to your estate?</h2>
                <p style={styles.ctaText}>
                  We’ll review your wage workflow, inventory process and reporting needs — and show how TeaSof fits.
                </p>
              </div>

              <div className="d-flex gap-2 flex-wrap">
                <Button
                  variant="light"
                  style={{ borderRadius: 12, fontWeight: 950 }}
                  onClick={() => navigate("/request-demo")}
                >
                  Request Demo
                </Button>
                <Button
                  variant="primary"
                  style={{
                    borderRadius: 12,
                    fontWeight: 950,
                    background: theme.accent2,
                    borderColor: theme.accent2,
                  }}
                  onClick={() => navigate("/contact-sales")}
                >
                  Contact Sales
                </Button>
              </div>
            </div>
          </div>

          <div style={styles.footer}>
            © {new Date().getFullYear()} {brand.name}. All rights reserved.
          </div>
        </section>
      </Container>
    </div>
  );
};

export default TeaSof;
